// Formatting utilities

export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as +91 XXXXX XXXXX
  if (cleaned.length === 10) {
    return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  }
  
  return phone;
};

export const maskPhoneNumber = (phone: string): string => {
  // Show only last 2 digits: +91 XXXXXXXX42
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+91 ${'X'.repeat(8)}${cleaned.slice(-2)}`;
  }
  return phone;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

export const formatTime = (time: string): string => {
  // Format time like "7:30 AM"
  return time;
};

export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

export const capitalizeFirst = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const formatBookingId = (id: string): string => {
  return id.toUpperCase();
};

/**
 * Format a duration (in minutes) into a human-readable string.
 * Examples: 45 → "45 min", 90 → "1 hr 30 min", 1500 → "1 day 1 hr"
 */
export const formatDuration = (minutes: number): string => {
  if (!minutes || minutes <= 0) return '--';

  const totalMin = Math.round(minutes);

  if (totalMin < 60) {
    return `${totalMin} min`;
  }

  const days = Math.floor(totalMin / 1440);
  const hrs = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);
  if (hrs > 0) parts.push(`${hrs} hr${hrs > 1 ? 's' : ''}`);
  if (mins > 0 && days === 0) parts.push(`${mins} min`);

  return parts.join(' ') || '--';
};

/**
 * Format duration range with a buffer time
 * @param durationMinutes - The base duration in minutes from API
 * @param bufferMinutes - Additional buffer time (default: 20 minutes)
 * @returns Formatted string like "23-43 min" or "1-2 hrs"
 */
export const formatDurationRange = (durationMinutes: number, bufferMinutes: number = 20): string => {
  if (!durationMinutes || durationMinutes <= 0) {
    return '--';
  }

  const minDuration = Math.round(durationMinutes);
  const maxDuration = Math.round(durationMinutes + bufferMinutes);

  // If duration crosses 60 minutes, convert to hours
  if (maxDuration >= 60) {
    const minHours = Math.floor(minDuration / 60);
    const minRemainingMins = minDuration % 60;
    const maxHours = Math.floor(maxDuration / 60);
    const maxRemainingMins = maxDuration % 60;

    const formatHoursMins = (hours: number, mins: number): string => {
      if (hours === 0) {
        return `${mins} min`;
      }
      if (mins === 0) {
        return `${hours} hr${hours > 1 ? 's' : ''}`;
      }
      return `${hours} hr${hours > 1 ? 's' : ''} ${mins} min`;
    };

    return `${formatHoursMins(minHours, minRemainingMins)} - ${formatHoursMins(maxHours, maxRemainingMins)}`;
  }

  return `${minDuration} - ${maxDuration} min`;
};

/**
 * Calculate estimated pickup time range based on shift start time and travel duration
 * @param shiftStartTime - Shift start time in format "HH:MM:SS" (e.g., "08:00:00")
 * @param durationMinutes - Travel duration in minutes from distance API
 * @param bufferMinutes - Additional buffer time in minutes (default: 20)
 * @returns Formatted time range like "7:25 - 7:45 AM" or null if calculation fails
 */
export const calculateEstimatedPickupTime = (
  shiftStartTime: string | undefined,
  durationMinutes: number | undefined,
  bufferMinutes: number = 20
): string | null => {
  // Validate inputs
  if (!shiftStartTime || !durationMinutes || durationMinutes <= 0) {
    return null;
  }

  try {
    // Parse the shift start time
    const [hours, minutes, seconds] = shiftStartTime.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    // Create a date object for shift start time (using today as reference)
    const shiftStart = new Date();
    shiftStart.setHours(hours, minutes, seconds || 0, 0);

    // Calculate min and max travel time with buffer
    const minTravelTime = Math.round(durationMinutes);
    const maxTravelTime = Math.round(durationMinutes + bufferMinutes);

    // Calculate pickup time range by subtracting travel time from shift start
    const minPickupTime = new Date(shiftStart.getTime() - maxTravelTime * 60000);
    const maxPickupTime = new Date(shiftStart.getTime() - minTravelTime * 60000);

    // Format time as "h:mm A" (e.g., "7:25 AM")
    const formatTime = (date: Date): string => {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    };

    return `${formatTime(minPickupTime)} - ${formatTime(maxPickupTime)}`;
  } catch (error) {
    console.error('Error calculating estimated pickup time:', error);
    return null;
  }
};
